/**
 * AllBaise Deals Hub — Groupon-style deal marketplace
 * Deal types: Daily Deal, Areas Deal, Group Buy, First Come, Bid Deal
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";
import {
  Loader2, Tag, MapPin, Clock, Users, Gavel,
  TrendingUp, Flame, ShoppingCart, Filter,
  Search, Plus, X, DollarSign, Check, Percent,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DEAL_TYPES = {
  daily_deal: { name: "Daily Deal", icon: Flame, color: "from-red-500 to-orange-600" },
  areas_deal: { name: "Areas Deal", icon: MapPin, color: "from-blue-500 to-cyan-600" },
  group_buy: { name: "Group Buy", icon: Users, color: "from-emerald-500 to-teal-600" },
  first_come: { name: "First Come", icon: TrendingUp, color: "from-violet-500 to-purple-600" },
  bid_deal: { name: "Bid Deal", icon: Gavel, color: "from-amber-500 to-orange-600" },
};

const CATEGORIES = [
  "Food & Drink", "Health & Beauty", "Travel & Hotels", "Electronics",
  "Fashion", "Home & Garden", "Automotive", "Education", "Services",
  "Entertainment", "Groceries", "Other"
];

function DealCard({ deal, onBuy, onCommit, onBid, user }) {
  const dt = DEAL_TYPES[deal.deal_type] || { name: deal.deal_type, icon: Tag, color: "from-amber-600 to-orange-600" };
  const TypeIcon = dt.icon;
  const saved = Math.round((1 - deal.deal_price / deal.original_price) * 100);
  const soldOut = deal.quantity_available !== null && deal.quantity_sold >= deal.quantity_available;
  const [bidAmount, setBidAmount] = useState((deal.current_bid || deal.starting_bid || 0) + (deal.bid_increment || 1));

  return (
    <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 overflow-hidden hover:border-violet-500/30 transition-all">
      <div className={`bg-gradient-to-r ${dt.color} p-4 relative`}>
        <div className="flex items-center justify-between">
          <Badge className="bg-white/20 text-white text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1">
            <TypeIcon className="w-3 h-3" /> {dt.name}
          </Badge>
          {saved > 0 && (
            <Badge className="bg-red-500/80 text-white text-[9px] px-2 py-0.5">
              <Percent className="w-2.5 h-2.5 mr-0.5" /> {saved}% OFF
            </Badge>
          )}
        </div>
        <h3 className="text-white font-bold text-sm mt-2 line-clamp-2">{deal.title}</h3>
        {deal.location && (
          <p className="text-white/70 text-[10px] flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3" /> {deal.location}
          </p>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-end gap-2">
          <span className="text-xl font-bold text-app">R{deal.deal_price}</span>
          <span className="text-sm text-zinc-500 line-through">R{deal.original_price}</span>
          <span className="text-[10px] text-emerald-500 font-medium ml-auto">
            Save R{deal.original_price - deal.deal_price}
          </span>
        </div>

        <p className="text-xs text-zinc-500 line-clamp-2">{deal.description}</p>

        {deal.highlights && deal.highlights.length > 0 && (
          <div className="space-y-1">
            {deal.highlights.slice(0, 3).map((h, i) => (
              <p key={i} className="text-[10px] text-zinc-400 flex items-start gap-1">
                <Check className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /> {h}
              </p>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 text-[10px] text-zinc-500 pt-1 border-t border-white/10 dark:border-white/10 light:border-gray-200">
          {deal.quantity_available !== null && (
            <span className="flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" />
              {deal.quantity_available - deal.quantity_sold} left
            </span>
          )}
          {deal.expires_at && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(deal.expires_at).toLocaleDateString()}
            </span>
          )}
          <span className="ml-auto text-[9px] text-zinc-600">by {deal.poster_name || "Anonymous"}</span>
        </div>

        {deal.deal_type === "group_buy" && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-zinc-500">Commitments: {deal.commitments?.length || 0}</span>
              {deal.min_commitments && <span className="text-zinc-500">Min: {deal.min_commitments}</span>}
            </div>
            <Button onClick={() => onCommit(deal.id)} size="sm" variant="outline"
              className="w-full text-[10px] h-7 border-emerald-600 text-emerald-500 hover:bg-emerald-600/10">
              <Users className="w-3 h-3 mr-1" /> Commit to Group Buy
            </Button>
          </div>
        )}

        {deal.deal_type === "bid_deal" && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-zinc-500">Current Bid: <strong className="text-amber-500">R{deal.current_bid || deal.starting_bid || 0}</strong></span>
              <span className="text-zinc-500">Bids: {deal.bids?.length || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" value={bidAmount} onChange={e => setBidAmount(Number(e.target.value))}
                className="h-7 text-xs bg-amber-950/40 border-amber-800/40" />
              <Button onClick={() => onBid(deal.id, bidAmount)} size="sm"
                className="h-7 text-[10px] bg-amber-600 hover:bg-amber-500 text-white">
                <Gavel className="w-3 h-3 mr-1" /> Bid
              </Button>
            </div>
          </div>
        )}

        {deal.deal_type !== "group_buy" && deal.deal_type !== "bid_deal" && (
          <Button onClick={() => onBuy(deal.id)} disabled={soldOut}
            className={`w-full h-8 text-xs ${soldOut ? "bg-zinc-700" : "bg-violet-600 hover:bg-violet-500"} text-white`}>
            {soldOut ? "Sold Out" : deal.deal_type === "first_come" ? "Claim Now" : "Buy Deal"}
          </Button>
        )}

        {deal.poster_verified && (
          <div className="flex items-center gap-1 text-[9px] text-emerald-500">
            <Badge className="bg-emerald-600/20 text-emerald-400 text-[8px] px-1 py-0">Verified Seller</Badge>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function DealsHub() {
  const auth = useAuth();
  const user = auth.user;
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [dealType, setDealType] = useState("all");
  const [country, setCountry] = useState("all");
  const [region, setRegion] = useState("all");
  const [radiusKm, setRadiusKm] = useState("");
  const [province, setProvince] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", deal_type: "daily_deal", category: "Food & Drink", original_price: "", deal_price: "", quantity_available: "", location: "", highlights: "", fine_print: "", min_commitments: "", starting_bid: "", bid_increment: "" });
  const [creating, setCreating] = useState(false);

  const fetchDeals = () => {
    setLoading(true);
    axios.get(`${API}/deals/list`, { headers: getHeaders() })
      .then(res => setDeals(res.data.items || []))
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDeals(); }, []);

  const filtered = deals.filter(d => {
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) && !d.description.toLowerCase().includes(search.toLowerCase())) return false;
    if (category !== "all" && d.category !== category) return false;
    if (dealType !== "all" && d.deal_type !== dealType) return false;
    if (province !== "all" && d.province !== province) return false;
    if (country !== "all" && d.country !== country) return false;
    if (region !== "all" && d.province !== region) return false;
    if (radiusKm && d.distance_km && parseFloat(d.distance_km) > parseFloat(radiusKm)) return false;
    if (region !== "all" && d.province !== region) return false;
    return d.status === "active";
  });

  const handleCreate = async () => {
    if (!form.title || !form.deal_price || !form.original_price) {
      toast.error("Title and prices are required"); return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        original_price: parseFloat(form.original_price),
        deal_price: parseFloat(form.deal_price),
        quantity_available: form.quantity_available ? parseInt(form.quantity_available) : null,
        min_commitments: form.min_commitments ? parseInt(form.min_commitments) : null,
        starting_bid: form.starting_bid ? parseFloat(form.starting_bid) : null,
        bid_increment: form.bid_increment ? parseFloat(form.bid_increment) : null,
        highlights: form.highlights ? form.highlights.split(",").map(h => h.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API}/deals/create`, payload, { headers: getHeaders() });
      toast.success("Deal created!");
      setShowCreate(false);
      setForm({ title: "", description: "", deal_type: "daily_deal", category: "Food & Drink", original_price: "", deal_price: "", quantity_available: "", location: "", highlights: "", fine_print: "", min_commitments: "", starting_bid: "", bid_increment: "" });
      fetchDeals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create deal"); }
    setCreating(false);
  };

  const handleBuy = async (dealId) => {
    try {
      await axios.post(`${API}/deals/${dealId}/buy`, {}, { headers: getHeaders() });
      toast.success("Deal purchased!");
      fetchDeals();
    } catch (e) { toast.error(e.response?.data?.detail || "Purchase failed"); }
  };

  const handleCommit = async (dealId) => {
    try {
      await axios.post(`${API}/deals/${dealId}/commit`, {}, { headers: getHeaders() });
      toast.success("Committed to group buy!");
      fetchDeals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to commit"); }
  };

  const handleBid = async (dealId, amount) => {
    try {
      await axios.post(`${API}/deals/${dealId}/bid?amount=${amount}`, {}, { headers: getHeaders() });
      toast.success("Bid placed!");
      fetchDeals();
    } catch (e) { toast.error(e.response?.data?.detail || "Bid failed"); }
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-400" /> Deals Hub
            </h1>
            <p className="text-xs text-zinc-500">Groupon-style marketplace for exclusive deals</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-amber-600 hover:bg-amber-500 text-white text-xs h-8">
            {showCreate ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            {showCreate ? "Cancel" : "Post a Deal"}
          </Button>
        </div>

        {showCreate && (
          <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-4 mb-4">
            <h2 className="text-sm font-semibold mb-3">New Deal</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Deal Type</label>
                <select value={form.deal_type} onChange={e => setForm({...form, deal_type: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500">
                  {Object.entries(DEAL_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Category</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Original Price *</label>
                <input type="number" value={form.original_price} onChange={e => setForm({...form, original_price: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Deal Price *</label>
                <input type="number" value={form.deal_price} onChange={e => setForm({...form, deal_price: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Quantity</label>
                <input type="number" value={form.quantity_available} onChange={e => setForm({...form, quantity_available: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-500">Location</label>
                <input type="text" value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                  className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
              </div>
              {form.deal_type === "group_buy" && (
                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-500">Min Commitments</label>
                  <input type="number" value={form.min_commitments} onChange={e => setForm({...form, min_commitments: e.target.value})}
                    className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
                </div>
              )}
              {form.deal_type === "bid_deal" && (
                <>
                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-500">Starting Bid</label>
                    <input type="number" value={form.starting_bid} onChange={e => setForm({...form, starting_bid: e.target.value})}
                      className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-500">Bid Increment</label>
                    <input type="number" value={form.bid_increment} onChange={e => setForm({...form, bid_increment: e.target.value})}
                      className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
                  </div>
                </>
              )}
            </div>
            <div className="space-y-1 mt-3">
              <label className="text-[10px] text-zinc-500">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
                className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
            </div>
            <div className="space-y-1 mt-2">
              <label className="text-[10px] text-zinc-500">Highlights (comma separated)</label>
              <input type="text" value={form.highlights} onChange={e => setForm({...form, highlights: e.target.value})}
                className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-white border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-amber-500" />
            </div>
            <Button onClick={handleCreate} disabled={creating}
              className="mt-3 bg-amber-600 hover:bg-amber-500 text-white text-xs h-8">
              {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
              Create Deal
            </Button>
          </Card>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..."
              className="w-full bg-zinc-800/50 dark:bg-zinc-800/50 light:bg-gray-100 border border-zinc-700 dark:border-zinc-700 light:border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-zinc-500 focus:outline-none focus:border-amber-500" />
          </div>
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setDealType("all")}
              className={`text-[10px] px-2.5 py-1 rounded-full ${dealType === "all" ? "bg-amber-600/30 text-amber-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>All</button>
            {Object.entries(DEAL_TYPES).map(([k, v]) => (
              <button key={k} onClick={() => setDealType(k)}
                className={`text-[10px] px-2.5 py-1 rounded-full ${dealType === k ? "bg-amber-600/30 text-amber-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>{v.name}</button>
            ))}
          </div>
        </div>

        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
          <button onClick={() => setCategory("all")}
            className={`text-[10px] px-3 py-1 rounded-full whitespace-nowrap ${category === "all" ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>All Categories</button>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`text-[10px] px-3 py-1 rounded-full whitespace-nowrap ${category === c ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500 hover:text-zinc-300"}`}>{c}</button>
          ))}
        </div>

        <GeoFilterBar country={country} onCountryChange={setCountry} region={region} onRegionChange={setRegion} radius={radiusKm} onRadiusChange={setRadiusKm} />

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-8 text-center">
            <Tag className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No deals found{search ? " matching your search" : " yet"}</p>
            <Button onClick={() => setShowCreate(true)} variant="outline" size="sm" className="mt-3 text-xs border-amber-600 text-amber-500">
              <Plus className="w-3 h-3 mr-1" /> Be the first to post a deal
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(deal => (
              <DealCard key={deal.id} deal={deal} onBuy={handleBuy} onCommit={handleCommit} onBid={handleBid} user={user} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
